import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Stack,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";
import SimpleTemplatePreview from "./SimpleTemplatePreview";
import { useDialog } from "../../context/DialogContext";

// Icons
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";

const PromptPreviewer = () => {
  const { showMediaDialog } = useDialog();
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Popover state
  const [anchorEl, setAnchorEl] = useState(null);

  // Example prompts
  const examplePrompts = [
    {
      label: "Smartphone Launch",
      prompt:
        "Create a promotional template for a new smartphone launch including body text with key features, a footer with company info, and buttons for 'Learn More' URL and 'Buy Now' quick reply.",
    },
    {
      label: "Seasonal Sales",
      prompt:
        "Generate a newsletter template for seasonal sales with example body texts highlighting discounts, a footer disclaimer, and buttons for 'Shop Now' URL and 'View Catalog' phone number.",
    },
    {
      label: "Customer Feedback",
      prompt:
        "Design a customer feedback request template with body text examples asking for ratings, no footer, and buttons for 'Yes' and 'No' quick replies.",
    },
    {
      label: "Event Invitation",
      prompt:
        "Build an event invitation template with body text for event details, a footer with date and time, and buttons for 'RSVP Yes' quick reply and 'Get Directions' URL.",
    },
  ];

  const handleSubmit = async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    setError("");
    setResponse(null);

    try {
      const res = await fetch(
        `${API_ENDPOINT}/agent/maketing-template-creator`,
        {
          method: "POST",
          ...getHeader(),
          body: JSON.stringify({ message: prompt }),
        }
      );

      if (!res.ok) throw new Error("Failed to fetch response");
      const data = await res.json();
      setResponse(data);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleMediaClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleSelectMedia = (mediaType) => {
    handleClosePopover();

    let mediaTypeConfig;
    if (mediaType === "image") {
      mediaTypeConfig = { mediaType: "image" };
    } else if (mediaType === "video") {
      mediaTypeConfig = { mediaType: "video" };
    } else if (mediaType === "document") {
      mediaTypeConfig = { mediaType: "document" }; // treat as pdf in preview
    }

    showMediaDialog("Select Media", "Please select the media you want to use", {
      isRcs: true,
      ...mediaTypeConfig,
      selection: "single",
      selectionLimit: 1,
      onConfirm: async (mode) => {
        console.log("Selected media URL:", mode[0]?.previewUrl);
        const url = mode[0]?.previewUrl || "";
        setSelectedMedia({ url, type: mediaTypeConfig.mediaType });
      },
    });
  };

  const open = Boolean(anchorEl);

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        {/* Left Side: Prompt Input */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Enter Prompt
          </Typography>

          {/* Example Prompts */}
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Example Prompts:
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ maxWidth: "100%", overflowX: "auto", mb: 2, pb: 1 }}
          >
            {examplePrompts.map((ex, index) => (
              <Button
                key={index}
                variant="outlined"
                size="small"
                onClick={() => setPrompt(ex.prompt)}
                sx={{ whiteSpace: "nowrap", minWidth: "auto" }}
              >
                {ex.label}
              </Button>
            ))}
          </Stack>

          <TextField
            fullWidth
            multiline
            rows={5}
            placeholder="Type your prompt here..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            variant="outlined"
          />

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} mt={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : "Generate Template"}
            </Button>

            <Button
              variant="outlined"
              color="success"
              startIcon={<CheckCircleIcon />}
              disabled={!response}
            >
              Send for Approval
            </Button>

            <Button
              variant="outlined"
              startIcon={<AddToPhotosIcon />}
              onClick={handleMediaClick}
            >
              Use Media
            </Button>
          </Stack>

          {error && (
            <Box mt={2}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
        </Grid>

        {/* Right Side: JSON Preview */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            API Response Preview
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              minHeight: 200,
              maxHeight: 500,
              overflow: "auto",
              backgroundColor: "#f9f9f9",
              fontFamily: "monospace",
              fontSize: "0.9rem",
              borderRadius: 2,
            }}
          >
            {response ? (
              <SimpleTemplatePreview
                template={response}
                media={selectedMedia}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {loading ? "Fetching response..." : "No response yet"}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Popover for Media Selection */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <List dense>
          <ListItemButton onClick={() => handleSelectMedia("image")}>
            <ListItemIcon>
              <ImageIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Image" />
          </ListItemButton>

          <ListItemButton onClick={() => handleSelectMedia("video")}>
            <ListItemIcon>
              <VideoLibraryIcon color="secondary" />
            </ListItemIcon>
            <ListItemText primary="Video" />
          </ListItemButton>

          <ListItemButton onClick={() => handleSelectMedia("document")}>
            <ListItemIcon>
              <PictureAsPdfIcon color="error" />
            </ListItemIcon>
            <ListItemText primary="Document (PDF)" />
          </ListItemButton>
        </List>
      </Popover>
    </Box>
  );
};

export default PromptPreviewer;
